// src/components/site-editor/ArrayEditor.jsx
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Array Editor - Handles arrays with whitelist-based field editing
 *
 * In read-only mode (readOnly=true):
 * - No add/remove buttons
 * - Only shows editable fields within each item (based on editableFields whitelist)
 * - Users can only edit text within existing items
 */
const ArrayEditor = ({ label, items = [], path, onChange, editableFields = [], readOnly = false }) => {

  // Detect what type of items are in the array
  const getItemType = () => {
    if (items.length === 0) return 'string'; // Default to string
    const firstItem = items[0];
    if (typeof firstItem === 'object' && firstItem !== null) return 'object';
    if (typeof firstItem === 'number') return 'number';
    return 'string';
  };

  const itemType = getItemType();

  // Get template for new items
  const getNewItemTemplate = () => {
    if (itemType === 'object') {
      // Create template based on first item's structure
      if (items.length > 0) {
        const template = {};
        Object.keys(items[0]).forEach(key => {
          const value = items[0][key];
          if (typeof value === 'string') template[key] = '';
          else if (typeof value === 'number') template[key] = 0;
          else if (typeof value === 'boolean') template[key] = false;
          else template[key] = '';
        });
        return template;
      }
      return {}; // Empty object if no items
    }
    if (itemType === 'number') return 0;
    return ''; // String
  };

  // Handle add item
  const handleAddItem = () => {
    const newItems = [...items, getNewItemTemplate()];
    onChange(path, newItems);
    setIsAdding(false);
  };

  // Handle remove item
  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(path, newItems);
  };

  // Handle update item
  const handleUpdateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onChange(path, newItems);
  };

  // Handle update nested field in object item
  const handleUpdateNestedField = (index, fieldKey, value) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [fieldKey]: value
    };
    onChange(path, newItems);
  };

  // Render item based on type
  const renderItem = (item, index) => {
    if (itemType === 'object') {
      return (
        <div key={index} className="space-y-3 border border-border rounded-lg p-4 bg-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">
              Item {index + 1}
            </span>
            {!readOnly && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveItem(index)}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Only render fields that are in the editableFields whitelist */}
          {Object.entries(item)
            .filter(([key]) => editableFields.length === 0 || editableFields.includes(key))
            .map(([key, value]) => {
              const isLongText = typeof value === 'string' && value.length > 100;
              const fieldLabel = key
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .replace(/^./, str => str.toUpperCase())
                .trim();

              return (
                <div key={key} className="space-y-2">
                  <Label className="text-xs">{fieldLabel}</Label>
                  {isLongText ? (
                    <Textarea
                      value={value || ''}
                      onChange={(e) => handleUpdateNestedField(index, key, e.target.value)}
                      placeholder={`Enter ${fieldLabel.toLowerCase()}...`}
                      rows={4}
                    />
                  ) : (
                    <Input
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = typeof item[key] === 'number'
                          ? parseFloat(e.target.value) || 0
                          : e.target.value;
                        handleUpdateNestedField(index, key, newValue);
                      }}
                      placeholder={`Enter ${fieldLabel.toLowerCase()}...`}
                      type={typeof item[key] === 'number' ? 'number' : 'text'}
                    />
                  )}
                </div>
              );
            })}
        </div>
      );
    }

    // Simple string/number items (shouldn't happen in read-only mode, but handle anyway)
    return (
      <div key={index} className="flex items-center gap-2">
        <Input
          value={item || ''}
          onChange={(e) => {
            const newValue = itemType === 'number'
              ? parseFloat(e.target.value) || 0
              : e.target.value;
            handleUpdateItem(index, newValue);
          }}
          placeholder={`Item ${index + 1}`}
          type={itemType === 'number' ? 'number' : 'text'}
          className="flex-1"
        />
        {!readOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRemoveItem(index)}
            className="h-9 w-9 p-0 text-destructive hover:text-destructive flex-shrink-0"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">
          {label} ({items.length} {items.length === 1 ? 'item' : 'items'})
        </Label>
      </div>

      {/* Items List */}
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-border rounded-lg">
            {readOnly ? 'No items' : 'No items yet. Click "Add Item" to get started.'}
          </div>
        ) : (
          items.map((item, index) => renderItem(item, index))
        )}
      </div>

      {/* Add Button - only show if not read-only */}
      {!readOnly && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddItem}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      )}
    </div>
  );
};

export default ArrayEditor;
